import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Check } from 'lucide-react-native';
import { useTheme } from '@/hooks/useTheme';

interface PriceOfferRowProps {
  id: string;
  selected: boolean;
  onSelect: (id: string) => void;
  badge?: string;
  icon?: React.ReactNode;
  title: string;
  price: string;
  description: string;
  disabled?: boolean;
}

export const PriceOfferRow: React.FC<PriceOfferRowProps> = ({
  id,
  selected,
  onSelect,
  badge,
  icon,
  title,
  price,
  description,
  disabled = false,
}) => {
  const { colors: { primary: primaryColor } } = useTheme();

  return (
    <TouchableOpacity
      style={[
        styles.container,
        selected && { borderColor: primaryColor, backgroundColor: 'rgba(255,255,255,0.06)' },
      ]}
      onPress={() => onSelect(id)}
      activeOpacity={0.7}
      disabled={disabled}
    >
      <View style={styles.radioColumn}>
        {selected ? (
          <View style={[styles.radioSelected, { backgroundColor: primaryColor }]}>
            <Check size={14} color="#ffffff" strokeWidth={3} />
          </View>
        ) : (
          <View style={styles.radioEmpty} />
        )}
      </View>

      <View style={styles.contentColumn}>
        {badge && (
          <View style={[styles.badge, { backgroundColor: primaryColor }]}>
            <Text style={styles.badgeText}>{badge}</Text>
          </View>
        )}

        <View style={styles.titleRow}>
          {icon && <View style={styles.icon}>{icon}</View>}
          <Text style={styles.title}>{title}</Text>
        </View>

        {price ? <Text style={styles.price}>{price}</Text> : null}
        <Text style={styles.description}>{description}</Text>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    backgroundColor: 'rgba(40, 40, 40, 0.85)',
    borderRadius: 12,
    padding: 14,
    borderWidth: 2,
    borderColor: 'transparent',
    marginBottom: 10,
  },
  radioColumn: {
    justifyContent: 'center',
    marginRight: 12,
    paddingTop: 2,
  },
  radioEmpty: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#555',
  },
  radioSelected: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  contentColumn: {
    flex: 1,
  },
  badge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 6,
    marginBottom: 6,
  },
  badgeText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  icon: {
    marginRight: 6,
  },
  title: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  price: {
    color: '#ffffff',
    fontSize: 20,
    fontWeight: '800',
    marginBottom: 4,
  },
  description: {
    color: '#aaa',
    fontSize: 12,
    lineHeight: 16,
    ...(Platform.OS === 'web' ? { maxWidth: 280 } : {}),
  },
});
